import { Ref, forwardRef } from "react"
import { Formik, FormikHelpers, FormikProps } from "formik"
import * as yup from "yup"
import { StandardFormikErrorScrollWrapper } from "../../../../../shared/components/forms/StandardFormikErrorScrollWrapper"
import { useAppLocalization } from "../../../../localization/components/AppLocalizationProvider"
import { toCurrency, toDineroLossy, toMonetaryAmount } from "../../../../currency/currencyLib"
import { CreateUpdateProductFormContent } from "./CreateUpdateProductFormContent"
import { CreateProduct, CreateUpdateProductFormValues } from "../productsTypes"

type CreateUpdateProductFormProps = Readonly<{
    initialValues?: CreateUpdateProductFormValues | null
    onSubmit: (formData: CreateProduct) => void
}>
type FormikRef = Ref<FormikProps<CreateUpdateProductFormValues>>

export const CreateUpdateProductForm = forwardRef(
    ({ initialValues, onSubmit }: CreateUpdateProductFormProps, ref: FormikRef) => {
        const { l10n } = useAppLocalization()

        const defaultInitialValues = {
            productGroup: null,
            productNumber: "",
            name: "",
            price: undefined,
            currency: "DKK",
            shops: [],
            description: "",
            isActive: true,
            availabilityDetails: {
                allowPreOrder: false,
            },
            paymentDetails: {
                isPriceHidden: false,
            },
            productGroupCode: undefined,
            itemLimits: null,
        }

        const validationSchema = yup.object({
            productGroup: yup
                .object()
                .shape({
                    id: yup.number(),
                    name: yup.string(),
                    isActive: yup.boolean(),
                })
                .required(l10n.getString("required-field")),
            productNumber: yup.string(),
            name: yup.string().required(l10n.getString("required-field")),
            price: yup.number().required(l10n.getString("required-field")),
            currency: yup.string(),
            shops: yup.array().of(
                yup.object().shape({
                    id: yup.number(),
                    name: yup.string(),
                    isActive: yup.boolean(),
                }),
            ),
            //add characters limit not to let ppl send very big texts to server, maybe add that character limit to form field like we did in gopay app
            description: yup.string(),
            isActive: yup.boolean(),
            itemLimits: yup
                .object({
                    maxDailyItemsPerPerson: yup
                        .number()
                        .nullable()
                        .transform((value) => (isNaN(value) ? undefined : value))
                        .min(1, l10n.getString("product-create-item-limits-max-error"))
                        .optional(),
                    minItemsPerOrder: yup
                        .number()
                        .nullable()
                        .transform((value) => (isNaN(value) ? undefined : value))
                        .min(2, l10n.getString("product-create-item-limits-min-error"))
                        .optional(),
                })
                .nullable()
                .test("either-or", l10n.getString("product-create-item-limits-error"), function (value) {
                    return value === null || !!value.maxDailyItemsPerPerson || !!value.minItemsPerOrder
                }),
        })

        function handleSubmit(
            values: CreateUpdateProductFormValues,
            _helpers: FormikHelpers<CreateUpdateProductFormValues>,
        ) {
            const {
                productGroup,
                shops,
                price,
                productPhoto,
                productDescription,
                availabilityDetails,
                deadline,
                priceLists,
                deliveryDetails,
                itemLimits,
                productGroupCode,
                ...productValues
            } = values

            const productGroupCodeBody = productGroupCode
                ? {
                      productGroupCode: {
                          id: productGroupCode.id,
                      },
                  }
                : {}

            const modifyData = {
                ...productValues,
                ...productGroupCodeBody,
                price: toMonetaryAmount(toDineroLossy(values.price!, toCurrency(values.currency))),
                productGroup: {
                    id: productGroup?.id!,
                },
                shops: shops.map((shop) => ({
                    id: shop.id,
                })),
                ...(productDescription
                    ? {
                          productDescription: {
                              ...productDescription,
                              allergens: productDescription.allergens.map((allergen) => ({
                                  id: allergen.id,
                              })),
                          },
                      }
                    : {}),
                availabilityDetails: {
                    allowPreOrder: !availabilityDetails?.allowPreOrder,
                    ...(availabilityDetails?.dateInterval
                        ? {
                              dateInterval: {
                                  ...(availabilityDetails?.dateInterval?.from
                                      ? { from: availabilityDetails?.dateInterval?.from.toISODate() }
                                      : {}),
                                  ...(availabilityDetails?.dateInterval?.to
                                      ? { to: availabilityDetails?.dateInterval?.to.toISODate() }
                                      : {}),
                              },
                          }
                        : {}),
                },
                ...(deadline
                    ? {
                          deadline: {
                              purchases: {
                                  ...deadline.purchases,
                                  timeOfDay: deadline.purchases.timeOfDay.toFormat("HH:mm"),
                              },
                              cancellation: {
                                  ...deadline.cancellation,
                                  timeOfDay: deadline.cancellation.timeOfDay.toFormat("HH:mm"),
                              },
                          },
                      }
                    : {}),
                ...(priceLists
                    ? {
                          priceLists: priceLists.map((priceList) => ({
                              id: priceList.id,
                              adjustedPrice: priceList.adjustedPrice,
                          })),
                      }
                    : {}),
                ...(deliveryDetails
                    ? {
                          deliveryDetails: { location: { id: deliveryDetails?.location.id } },
                      }
                    : {}),
                ...(itemLimits
                    ? {
                          itemLimits: itemLimits,
                      }
                    : {}),
            }

            if (!productPhoto?.uid && productPhoto?.url) {
                onSubmit({
                    ...modifyData,
                    productPhoto: {
                        url: productPhoto?.url,
                    },
                })
            } else {
                onSubmit({
                    ...modifyData,
                    ...(productPhoto?.uid !== undefined
                        ? {
                              productPhoto: {
                                  uid: productPhoto?.uid,
                              },
                          }
                        : {}),
                })
            }
        }

        return (
            <>
                <Formik<CreateUpdateProductFormValues>
                    enableReinitialize
                    initialValues={initialValues ? initialValues : defaultInitialValues}
                    validationSchema={validationSchema}
                    onSubmit={handleSubmit}
                    innerRef={ref}
                    validateOnBlur={false}
                    validateOnChange={false}
                >
                    <StandardFormikErrorScrollWrapper<CreateUpdateProductFormValues>
                        children={<CreateUpdateProductFormContent />}
                    />
                </Formik>
            </>
        )
    },
)
